import { closeBrackets } from "@codemirror/autocomplete";
import { EditorSelection, type Extension, Prec } from "@codemirror/state";
import { EditorView } from "@codemirror/view";

/** Symmetric markdown markers we auto-pair ourselves. Brackets `()[]{}` and
 *  quotes are left to `@codemirror/autocomplete`'s `closeBrackets`. */
const MARKERS = new Set(["*", "_", "`", "="]);

/** Auto-pairing for symmetric markers, the core of the "type one asterisk and it
 *  closes itself" behaviour:
 *   - a non-empty selection is wrapped: `sel` → `*sel*`;
 *   - an empty cursor inserts the pair with the caret between: `|` → `*|*`;
 *   - typing the second marker of an open pair grows it: `*|*` → `**|**`
 *     (so `**` is ready for bold), and likewise `=|=` → `==|==`;
 *   - typing a marker immediately before its own auto-inserted closer steps over
 *     it instead of duplicating: `word|*` → `word*|`.
 *  No-ops during IME composition so it never corrupts CJK input. */
const markerInput = EditorView.inputHandler.of((view, from, to, text) => {
  if (view.composing) return false;
  const { state } = view;
  const range = state.selection.main;
  // Only act on ordinary typing that replaces the primary selection.
  if (from !== range.from || to !== range.to) return false;

  const before = from > 0 ? state.sliceDoc(from - 1, from) : "";
  const after = state.sliceDoc(from, from + 1);

  // "* " at line start is a bullet, not emphasis: when a space lands inside a
  // freshly auto-paired `*|*` whose `*` is the first non-space on the line, drop
  // the auto-inserted closer so the line becomes "* " (then "• " in preview).
  if (text === " " && from === to && before === "*" && after === "*") {
    const line = state.doc.lineAt(from);
    if (/^\s*$/.test(line.text.slice(0, from - 1 - line.from))) {
      view.dispatch(
        state.update({
          changes: { from, to: from + 1, insert: " " },
          selection: EditorSelection.cursor(from + 1),
          userEvent: "input.type",
        }),
      );
      return true;
    }
  }

  if (!MARKERS.has(text)) return false;

  if (from !== to) {
    const sel = state.sliceDoc(from, to);
    view.dispatch(
      state.update({
        changes: { from, to, insert: text + sel + text },
        selection: EditorSelection.range(from + text.length, to + text.length),
        userEvent: "input.type",
        scrollIntoView: true,
      }),
    );
    return true;
  }

  // Step over an auto-inserted closer instead of typing a duplicate — the VS
  // Code behaviour. Someone who types the closing markers out in full (`**bold`
  // then `**`, not trusting the auto-pair) must end up with `**bold**`, not
  // `**bold****`.
  //
  // The tricky case is that `*|*` and `**bold*|*` look identical one character
  // either side of the caret: marker before, marker after. The first is a fresh
  // empty pair that should GROW to `**|**`; the second is a closer being typed
  // out that should be STEPPED OVER. What tells them apart is whether the run of
  // markers to the caret's left is preceded by actual content — `bold` in the
  // second, nothing (or whitespace, for `foo *|*`) in the first.
  if (after === text) {
    let run = 0;
    while (from - run - 1 >= 0 && state.sliceDoc(from - run - 1, from - run) === text) run++;
    const beforeRun = from - run > 0 ? state.sliceDoc(from - run - 1, from - run) : "";
    const closingTypedOut = beforeRun !== "" && !/\s/.test(beforeRun);
    if (run === 0 || closingTypedOut) {
      view.dispatch(state.update({ selection: EditorSelection.cursor(from + 1), userEvent: "move" }));
      return true;
    }
  }

  // Insert a matching pair with the caret inside (covers fresh markers and the
  // double-marker growth, which is the same edit).
  view.dispatch(
    state.update({
      changes: { from, insert: text + text },
      selection: EditorSelection.cursor(from + text.length),
      userEvent: "input.type",
      scrollIntoView: true,
    }),
  );
  return true;
});

/** Auto-pairing: our symmetric-marker handler (high precedence so it wins over
 *  the default insertion) plus bracket/quote closing from the autocomplete pkg. */
export const autoPair: Extension = [Prec.high(markerInput), closeBrackets()];
